import { createContext, useContext, useEffect, useState } from "react";
import { BrowserRouter, Link, Navigate, NavLink, Route, Routes, useParams } from "react-router-dom";
import { BarChart3, Contact, Inbox, LayoutList, LifeBuoy, LogOut, Mail, Menu as MenuIcon, Monitor, Moon, PenLine, Settings as SettingsIcon, Sun } from "lucide-react";
import { api } from "./lib/api";
import { completeSignIn, idToken, loadConfig, signIn, signOut } from "./lib/auth";
import { applyTheme, getTheme, setTheme, watchSystemTheme, type ThemeChoice } from "./lib/theme";
import { Button, cx, Empty, ErrorNote, Loading } from "./components/ui";
import Today from "./pages/Today";
import Campaigns from "./pages/Campaigns";
import CampaignBuilder from "./pages/CampaignBuilder";
import CampaignDetail from "./pages/CampaignDetail";
import Exceptions from "./pages/Exceptions";
import Review from "./pages/Review";
import Contacts from "./pages/Contacts";
import ContactDetail from "./pages/ContactDetail";
import AccountDetail from "./pages/AccountDetail";
import Stats from "./pages/Stats";
import Outbox from "./pages/Outbox";
import Settings from "./pages/Settings";
import MyRules from "./pages/MyRules";

export interface Me {
  sub: string;
  email: string;
  name: string;
  isAdmin: boolean;
  groups: string[];
  profile?: { title?: string; phone?: string; name: string };
  settings: { offerings: string[]; killSwitch: boolean; aiKeySet: boolean; aiProvider?: string };
}
const MeContext = createContext<{ me: Me; refreshMe: () => Promise<void> } | null>(null);
export const useMe = () => useContext(MeContext)!;

applyTheme();
watchSystemTheme();

export default function App() {
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [needsSignIn, setNeedsSignIn] = useState(false);

  const refreshMe = async () => setMe(await api<Me>("/me"));
  useEffect(() => {
    (async () => {
      try {
        await loadConfig();
        const err = await completeSignIn();
        if (err) setError(err);
        if (!(await idToken())) return setNeedsSignIn(true);
        await refreshMe();
      } catch (e) {
        setError((e as Error).message);
      }
    })();
  }, []);

  if (needsSignIn)
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="w-full max-w-sm space-y-4 rounded-2xl border border-line bg-surface p-8 text-center">
          <div className="flex justify-center">
            <Logo />
          </div>
          <p className="text-sm text-muted">Sign in with your Intellias AI SDR account.</p>
          <ErrorNote error={error} />
          <Button tone="primary" className="w-full" onClick={() => signIn()}>
            Sign in
          </Button>
          <div className="border-t border-line pt-4 text-sm text-ink-2">
            New here?{" "}
            <button className="font-medium text-accent-text hover:underline" onClick={() => signIn({ signUp: true })}>
              Create an account
            </button>
            <div className="mt-1 text-xs text-muted">Use your @intellias.net email. We'll send a code to verify it.</div>
            <a className="mt-3 inline-block text-xs text-accent-text hover:underline" href="/help.htm">
              How AI SDR works
            </a>
          </div>
        </div>
      </div>
    );
  if (error && !me)
    return (
      <div className="mx-auto max-w-md p-8">
        <ErrorNote error={error} />
        <Button className="mt-4" onClick={() => signOut()}>
          Sign out
        </Button>
      </div>
    );
  if (!me)
    return (
      <div className="mx-auto max-w-3xl p-8">
        <Loading rows={6} />
      </div>
    );
  return (
    <MeContext.Provider value={{ me, refreshMe }}>
      <BrowserRouter>
        <Shell>
          <Routes>
            <Route path="/" element={<Today />} />
            <Route path="/campaigns" element={<Campaigns />} />
            <Route path="/campaigns/new" element={<CampaignBuilder />} />
            <Route path="/campaigns/:id" element={<CampaignDetail />} />
            <Route path="/campaigns/:id/review" element={<Exceptions />} />
            <Route path="/campaigns/:cid/contacts/:ct" element={<Review />} />
            <Route path="/review/:cid/:ct" element={<LegacyReview />} />
            <Route path="/contacts" element={<Contacts />} />
            <Route path="/contacts/:id" element={<ContactDetail />} />
            <Route path="/accounts/:id" element={<AccountDetail />} />
            <Route path="/stats" element={<Stats />} />
            <Route path="/rules" element={<MyRules />} />
            {me.isAdmin && <Route path="/admin/outbox" element={<Outbox />} />}
            {me.isAdmin && <Route path="/admin/settings/*" element={<Settings />} />}
            <Route path="/settings/*" element={<Navigate to="/admin/settings" replace />} />
            <Route path="/outbox" element={<Navigate to="/admin/outbox" replace />} />
            <Route path="/learning" element={<Navigate to="/admin/settings/learning" replace />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Shell>
      </BrowserRouter>
    </MeContext.Provider>
  );
}

function LegacyReview() {
  const { cid, ct } = useParams();
  return <Navigate to={`/campaigns/${cid}/contacts/${ct}`} replace />;
}

function NotFound() {
  return (
    <Empty
      title="Page not found"
      action={
        <Link to="/">
          <Button tone="primary">Go to Today</Button>
        </Link>
      }
    >
      That address doesn't match anything in AI SDR. It may be an old link.
    </Empty>
  );
}

function Logo() {
  return (
    <div className="flex items-center gap-2">
      <img src="/favicon.svg" alt="" className="h-7 w-7" />
      <div className="text-left leading-tight">
        <div className="text-sm font-bold text-ink">AI SDR</div>
        <div className="text-[11px] text-muted">Intellias</div>
      </div>
    </div>
  );
}

function NavItem({ to, icon: Icon, label, end, onClick }: { to: string; icon: typeof Inbox; label: string; end?: boolean; onClick?: () => void }) {
  return (
    <NavLink to={to} end={end} onClick={onClick} className={({ isActive }) => cx("flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm", isActive ? "bg-accent-soft font-medium text-accent-text" : "text-ink-2 hover:bg-surface-2")}>
      <Icon size={17} strokeWidth={1.75} />
      {label}
    </NavLink>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  const { me } = useMe();
  const [open, setOpen] = useState(false);
  const [theme, setThemeState] = useState<ThemeChoice>(getTheme());
  const close = () => setOpen(false);
  const pick = (t: ThemeChoice) => {
    setTheme(t);
    setThemeState(t);
  };
  return (
    <div className="min-h-screen md:flex">
      <aside className="border-b border-line bg-surface md:sticky md:top-0 md:flex md:h-screen md:w-60 md:shrink-0 md:flex-col md:border-b-0 md:border-r">
        <div className="flex items-center justify-between px-4 py-4">
          <Logo />
          <button className="rounded-md p-2 text-ink-2 md:hidden" onClick={() => setOpen(!open)} aria-label="Menu">
            <MenuIcon size={18} />
          </button>
        </div>
        <nav className={cx("flex-1 flex-col gap-0.5 px-3 pb-3 md:flex", open ? "flex" : "hidden")}>
          <NavItem to="/" end icon={Inbox} label="Today" onClick={close} />
          <NavItem to="/campaigns" icon={LayoutList} label="Campaigns" onClick={close} />
          <NavItem to="/contacts" icon={Contact} label="Contacts" onClick={close} />
          <NavItem to="/stats" icon={BarChart3} label="Stats" onClick={close} />
          <NavItem to="/rules" icon={PenLine} label="My writing rules" onClick={close} />
          {me.isAdmin && (
            <>
              <div className="mt-5 px-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted">Admin</div>
              <NavItem to="/admin/settings" icon={SettingsIcon} label="Settings" onClick={close} />
              <NavItem to="/admin/outbox" icon={Mail} label="Outbox" onClick={close} />
            </>
          )}
          <div className="mt-auto space-y-3 border-t border-line px-3 pt-4">
            <div className="text-sm">
              <div className="truncate font-medium text-ink">{me.profile?.name || me.name || me.email}</div>
              <div className="text-xs text-muted">
                {me.isAdmin ? "Admin" : "Rep"}
                {me.profile?.title ? ` · ${me.profile.title}` : ""}
              </div>
            </div>
            <div className="flex items-center gap-1 rounded-lg bg-surface-2 p-0.5" role="radiogroup" aria-label="Theme">
              {([["light", Sun], ["system", Monitor], ["dark", Moon]] as const).map(([t, Icon]) => (
                <button key={t} role="radio" aria-checked={theme === t} title={`${t[0]!.toUpperCase()}${t.slice(1)} theme`} onClick={() => pick(t)} className={cx("flex flex-1 justify-center rounded-md py-1", theme === t ? "bg-surface text-ink shadow-sm" : "text-muted")}>
                  <Icon size={14} />
                </button>
              ))}
            </div>
            <div className="flex gap-3 text-xs">
              <a className="flex items-center gap-1 text-accent-text hover:underline" href="/help.htm" target="_blank" rel="noreferrer">
                <LifeBuoy size={13} /> Help
              </a>
              <button className="flex items-center gap-1 text-accent-text hover:underline" onClick={() => signOut()}>
                <LogOut size={13} /> Sign out
              </button>
            </div>
          </div>
        </nav>
      </aside>
      <main className="min-w-0 flex-1">
        {me.settings.killSwitch && <div className="border-b border-line bg-surface-2 px-4 py-2 text-center text-sm text-ink-2">Kill switch is on: live sending is paused. Test and dry-run sends continue.</div>}
        <div className="mx-auto max-w-[1180px] space-y-6 px-4 py-6 md:px-8">{children}</div>
      </main>
    </div>
  );
}
